'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const VALUES = [
  { icon: '🔹', title: 'Meaningful Connections', desc: 'Build genuine relationships with people who share your mindset, goals, and desire for growth.' },
  { icon: '🔹', title: 'Personal Development', desc: 'Gain access to a supportive environment that encourages self-improvement, confidence-building, and continuous learning.' },
  { icon: '🔹', title: 'Support System', desc: 'Be part of a community that motivates, uplifts, and stands by you through challenges and successes.' },
  { icon: '🔹', title: 'Networking Opportunities', desc: 'Connect with individuals from different backgrounds, opening doors to new opportunities, collaborations, and ideas.' },
  { icon: '🔹', title: 'Accountability & Motivation', desc: 'Stay consistent with your goals by surrounding yourself with people who push you to do better and stay focused.' },
  { icon: '🔹', title: 'Safe & Positive Environment', desc: 'Enjoy a judgment-free space where you can express yourself, share experiences, and grow without fear.' },
  { icon: '🔹', title: 'Real-Life Impact', desc: 'Turn conversations into action — Conect is not just about talking, but about making real progress in your life.' },
];

export default function AboutPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-void pt-24 pb-20">
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 20%, rgba(123,47,247,0.10) 0%, transparent 70%)' }}
        />

        <div ref={sectionRef} className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">

          {/* Hero header */}
          <div className="text-center mb-16 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.4)] bg-[rgba(123,47,247,0.08)] mb-6">
              <span className="w-2 h-2 rounded-full" style={{ background: '#7B2FF7' }} />
              <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                Our Story
              </span>
            </div>
            <h1 className="font-serif font-bold mb-4" style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', color: 'var(--cream)' }}>
              Building Better Lives{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>Together</em>
            </h1>
            <p className="text-lg font-light italic" style={{ color: 'rgba(240,230,211,0.5)', fontFamily: 'Fraunces, serif' }}>
              "Connect with a Purpose."
            </p>
          </div>

          {/* Intro paragraph */}
          <div
            className="fade-up delay-100 rounded-3xl p-8 md:p-10 mb-8"
            style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
          >
            <p className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(240,230,211,0.75)' }}>
              Conect Club is a community-driven platform designed to bring people together with one shared purpose:{' '}
              <strong style={{ color: 'var(--cream)' }}>growth through connection.</strong> It is more than just a club — it is a supportive environment where individuals meet, share experiences, and uplift one another in their journey toward personal and collective improvement.
            </p>
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            <div
              className="fade-up delay-200 rounded-2xl p-7"
              style={{ background: 'rgba(123,47,247,0.08)', border: '1px solid rgba(123,47,247,0.25)' }}
            >
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#7B2FF7' }}>
                Our Mission
              </p>
              <h3 className="font-serif font-semibold text-xl mb-3" style={{ color: 'var(--cream)' }}>
                Empowering Growth Through Connection
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,230,211,0.6)' }}>
                At Conect Club, we believe that real progress happens when people are surrounded by the right network. We create spaces where every member feels seen, heard, and valued — empowering them to become better versions of themselves.
              </p>
            </div>
            <div
              className="fade-up delay-300 rounded-2xl p-7"
              style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
            >
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#7B2FF7' }}>
                Our Vision
              </p>
              <h3 className="font-serif font-semibold text-xl mb-3" style={{ color: 'var(--cream)' }}>
                A World Connected with Purpose
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,230,211,0.6)' }}>
                We envision a world where meaningful connections are accessible to everyone — where communities thrive on mutual support, shared goals, and authentic relationships that create lasting real-world impact.
              </p>
            </div>
          </div>

          {/* Community focus */}
          <div
            className="fade-up delay-200 rounded-2xl p-8 mb-8"
            style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
          >
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#7B2FF7' }}>
              Community Focus
            </p>
            <h3 className="font-serif font-semibold text-xl mb-4" style={{ color: 'var(--cream)' }}>
              More Than a Club. A Movement.
            </h3>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(240,230,211,0.65)' }}>
              The club encourages meaningful interactions, accountability, and collaboration. Members are empowered to exchange ideas, set goals, and motivate each other to become better versions of themselves. Through regular engagements, activities, and shared experiences, Conect fosters a culture of positivity, growth, and genuine connection.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,230,211,0.65)' }}>
              Whether it's personal development, career advancement, emotional support, or simply finding like-minded individuals, Conect creates a space where every member feels seen, heard, and valued.
            </p>
          </div>

          {/* Why Join section */}
          <div className="mb-8">
            <div className="text-center mb-8 fade-up">
              <h2 className="font-serif font-bold" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', color: 'var(--cream)' }}>
                Why Join{' '}
                <em className="not-italic" style={{ color: '#7B2FF7' }}>Conect Club?</em>
              </h2>
              <p className="mt-3 text-base font-light" style={{ color: 'rgba(240,230,211,0.5)' }}>
                Conect Club offers more than just connection — it provides real value that helps members grow and thrive.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {VALUES?.map((val, i) => (
                <div
                  key={val?.title}
                  className={`fade-up delay-${Math.min((i % 3 + 1) * 100, 300)} rounded-2xl p-5 flex gap-4`}
                  style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">{val?.icon}</span>
                  <div>
                    <h4 className="text-sm font-bold mb-1" style={{ color: '#7B2FF7' }}>{val?.title}</h4>
                    <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(240,230,211,0.55)' }}>{val?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quote banner */}
          <div
            className="fade-up rounded-3xl p-10 text-center"
            style={{ background: 'rgba(123,47,247,0.08)', border: '1px solid rgba(123,47,247,0.25)' }}
          >
            <blockquote className="font-serif text-xl md:text-2xl font-semibold mb-6" style={{ color: 'var(--cream)' }}>
              "At Conect Club, you don't just meet people —{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>you meet the right people.</em>"
            </blockquote>
            <a href="/verify" className="cta-pill px-8 py-3.5 text-sm font-semibold inline-block">
              Verify Your Membership
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
